import { ethers } from "ethers";
import Parse from "parse";
import PubSub from "pubsub-js";

import * as ClaimTopicsRegistry from "../abi/IClaimTopicsRegistry.json";
import * as IdentityFactory from "../abi/IdentityFactory.json";
import * as IdentityRegistry from "../abi/IIdentityRegistry.json";
import * as TrustedIssuersRegistry from "../abi/ITrustedIssuersRegistry.json";
import ParseClient from "../services/ParseClient"; // Import the singleton instance
import { NomyxEvent } from "../utils/Constants";

class BlockchainService {
  claimTopicsAbi = ClaimTopicsRegistry.default.abi;
  identityRegistryAbi = IdentityRegistry.default.abi;
  trustedIssuersRegistryAbi = TrustedIssuersRegistry.default.abi;
  identityFactoryAbi = IdentityFactory.default.abi;

  constructor(provider, contractAddress, identityRegistryAddress) {
    this.provider = provider;

    // ✅ Check if provider is Web3Provider (Wallet) or JsonRpcProvider (RPC Fallback)
    if (provider instanceof ethers.providers.Web3Provider) {
      console.log("🔹 Web3 Wallet Detected, setting signer...");
      this.signer = provider.getSigner();
    } else {
      if (provider instanceof ethers.providers.StaticJsonRpcProvider) {
        this.signer = null; // Read-only mode
        // Polling interval (and the contract.on(...) listeners below that
        // depend on it) is configured once, on this same provider instance,
        // in App.tsx's setupProvider() — do not touch `_pollingInterval`
        // here. Directly overwriting that private field (as this used to)
        // bypasses ethers' `pollingInterval` setter and re-breaks it.
      }
    }

    console.log("blockchain service provider: ", this.provider);
    console.log("blockchain service signer: ", this.signer);

    // ✅ Use the best available provider for contracts (signer for transactions, provider for reads)
    const contractProvider = this.signer || this.provider;

    // ✅ Initialize smart contracts
    this.claimTopicRegistryService = new ethers.Contract(contractAddress, this.claimTopicsAbi, contractProvider);
    this.identityRegistryService = new ethers.Contract(contractAddress, this.identityRegistryAbi, contractProvider);
    this.trustedIssuersRegistryService = new ethers.Contract(contractAddress, this.trustedIssuersRegistryAbi, contractProvider);
    this.identityFactoryService = new ethers.Contract(identityRegistryAddress, this.identityFactoryAbi, contractProvider);

    // Claim Topics Registry
    this.addClaimTopic = this.addClaimTopic.bind(this);
    this.removeClaimTopic = this.removeClaimTopic.bind(this);
    this.getClaimTopics = this.getClaimTopics.bind(this);

    // Identity Registry
    this.createIdentity = this.createIdentity.bind(this);
    this.getIdentity = this.getIdentity.bind(this);
    this.addIdentity = this.addIdentity.bind(this);
    this.getDigitalIdentity = this.getDigitalIdentity.bind(this);
    this.getActiveIdentities = this.getActiveIdentities.bind(this);
    this.getPendingIdentities = this.getPendingIdentities.bind(this);
    this.updateIdentity = this.updateIdentity.bind(this);
    this.createParseIdentity = this.createParseIdentity.bind(this);
    this.batchAddIdentity = this.batchAddIdentity.bind(this);
    this.removeIdentity = this.removeIdentity.bind(this);
    this.unregisterIdentity = this.unregisterIdentity.bind(this);
    this.softRemoveUser = this.softRemoveUser.bind(this);
    this.approveUser = this.approveUser.bind(this);
    this.setClaims = this.setClaims.bind(this);
    this.addClaim = this.addClaim.bind(this);
    this.removeClaim = this.removeClaim.bind(this);
    this.contains = this.contains.bind(this);
    this.isVerified = this.isVerified.bind(this);
    this.identity = this.identity.bind(this);
    this.getRegistryUsers = this.getRegistryUsers.bind(this);
    this.isRegistryUser = this.isRegistryUser.bind(this);
    this.getClaims = this.getClaims.bind(this);
    this.getClaim = this.getClaim.bind(this);
    this.hasClaim = this.hasClaim.bind(this);
    this.getTrustedIssuersByObjectId = this.getTrustedIssuersByObjectId.bind(this);

    // Trusted Issuers Registry
    this.addTrustedIssuer = this.addTrustedIssuer.bind(this);
    this.removeTrustedIssuer = this.removeTrustedIssuer.bind(this);
    this.updateIssuerClaimTopics = this.updateIssuerClaimTopics.bind(this);
    this.getTrustedIssuers = this.getTrustedIssuers.bind(this);
    this.isTrustedIssuer = this.isTrustedIssuer.bind(this);
    this.getTrustedIssuerClaimTopics = this.getTrustedIssuerClaimTopics.bind(this);
    this.hasClaimTopic = this.hasClaimTopic.bind(this);

    this.getTokenProjects = this.getTokenProjects.bind(this);

    this.claimTopicRegistryService.on(NomyxEvent.ClaimTopicAdded, (claimTopic) => PubSub.publish(NomyxEvent.ClaimTopicAdded, claimTopic));
    this.claimTopicRegistryService.on(NomyxEvent.ClaimTopicRemoved, (claimTopic) => PubSub.publish(NomyxEvent.ClaimTopicRemoved, claimTopic));
    this.trustedIssuersRegistryService.on(NomyxEvent.ClaimTopicsUpdated, (trustedIssuer, claimTopics) =>
      PubSub.publish(NomyxEvent.ClaimTopicsUpdated, {
        trustedIssuer,
        claimTopics,
      })
    );
    this.trustedIssuersRegistryService.on(NomyxEvent.TrustedIssuerAdded, (trustedIssuer, claimTopics) =>
      PubSub.publish(NomyxEvent.TrustedIssuerAdded, {
        trustedIssuer,
        claimTopics,
      })
    );
    this.trustedIssuersRegistryService.on(NomyxEvent.TrustedIssuerRemoved, (trustedIssuer) =>
      PubSub.publish(NomyxEvent.TrustedIssuerRemoved, trustedIssuer)
    );
    this.identityRegistryService.on(NomyxEvent.ClaimAdded, (claimId, topic, scheme, issuer, signature, data, uri) =>
      PubSub.publish(NomyxEvent.ClaimAdded, {
        claimId,
        topic,
        scheme,
        issuer,
        signature,
        data,
        uri,
      })
    );
    this.identityRegistryService.on(NomyxEvent.ClaimRemoved, (claimId, topic, scheme, issuer, signature, data, uri) =>
      PubSub.publish(NomyxEvent.ClaimRemoved, {
        claimId,
        topic,
        scheme,
        issuer,
        signature,
        data,
        uri,
      })
    );
    this.identityRegistryService.on(NomyxEvent.IdentityAdded, (address, identity) => PubSub.publish(NomyxEvent.IdentityAdded, { address, identity }));
    this.identityRegistryService.on(NomyxEvent.IdentityRemoved, (address, identity) =>
      PubSub.publish(NomyxEvent.IdentityRemoved, { address, identity })
    );
    this.identityRegistryService.on(NomyxEvent.IdentityCountryUpdated, (identity, country) =>
      PubSub.publish(NomyxEvent.IdentityCountryUpdated, { identity, country })
    );
    this.identityRegistryService.on(NomyxEvent.ClaimAdded, (identity, claimTopic, claim) =>
      PubSub.publish(NomyxEvent.ClaimAdded, { identity, claimTopic, claim })
    );
    this.identityRegistryService.on(NomyxEvent.ClaimRemoved, (identity, claimTopic) =>
      PubSub.publish(NomyxEvent.ClaimRemoved, { identity, claimTopic })
    );
  }

  // Event listeners
  publish(event, data) {
    PubSub.publish(event, data);
  }

  subscribe(event, handler) {
    return PubSub.subscribe(event, handler);
  }

  unsubscribe(token) {
    return PubSub.unsubscribe(token);
  }

  async addClaimTopic(claimTopic) {
    const contractWithSigner = this.claimTopicRegistryService.connect(this.signer);
    const tx = await contractWithSigner.addClaimTopic(claimTopic);
    return await tx.wait();
  }

  async updateClaimTopic(claimTopic) {
    const maxRetries = 3,
      delay = 2000;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Check if the record exists
        const existingRecord = await ParseClient.getRecords("ClaimTopic", [], [], ["topic"], 1, 0, "topic", "desc");
        if (existingRecord?.length > 0) {
          // Attempt to update the record
          await ParseClient.updateExistingRecord("ClaimTopic", ["topic"], [claimTopic.topic], claimTopic);

          console.log("Record updated successfully!");
          return; // Exit after successful update
        }
      } catch (error) {
        console.error(`Attempt ${attempt} failed: ${error.message}`);
      }
      // Wait for 1 second before retrying (if not the last attempt)
      if (attempt < maxRetries) await new Promise((res) => setTimeout(res, delay));
    }
    console.error("Max retry attempts reached. Failed to update the record.");
  }

  async removeClaimTopic(claimTopic) {
    const contractWithSigner = this.claimTopicRegistryService.connect(this.signer);
    const tx = await contractWithSigner.removeClaimTopic(claimTopic);
    await tx.wait();
    return tx;
  }

  async getClaimTopics() {
    return await ParseClient.getRecords("ClaimTopic", [], [], ["*"]);
  }

  async getNextClaimTopicId() {
    const result = await ParseClient.getRecords("ClaimTopic", [], [], ["topic"], 1, 0, "createdAt", "desc");
    let highestTopicId = result.length > 0 ? Number.parseInt(result[0].attributes.topic) + 1 : 1;
    return highestTopicId;
  }

  async getClaimTopicById(id) {
    const claimTopic = await ParseClient.getRecords("ClaimTopic", ["objectId"], [id], ["*"]);
    return claimTopic;
  }

  async getTrustedIssuersForClaimTopics(id) {
    const { issuers = [] } = await Parse.Cloud.run("getTrustedIssuers", { claimTopic: id });
    return issuers.map((issuer) => this._toIssuerRecord(issuer));
  }

  async getTrustedIssuersByObjectId(id) {
    const { issuers = [] } = await Parse.Cloud.run("getTrustedIssuers", { objectId: id });
    return issuers.length > 0 ? this._toIssuerRecord(issuers[0]) : null;
  }

  /**
   * Retrieves claims associated with a specific claim topic
   * @param {string} id - The object ID of the claim topic
   * @returns {Array} - Array of claims or empty array if none found
   */
  async getClaimsForClaimTopics(id) {
    try {
      // Validate input
      if (!id) {
        return [];
      }

      // Get the claim topic details
      const claimTopicObj = await ParseClient.getRecords("ClaimTopic", ["objectId"], [id]);
      if (!claimTopicObj || claimTopicObj.length === 0) {
        return [];
      }

      // Extract the topic value
      const claimTopicValue = claimTopicObj[0].attributes.topic;

      // Get claims using the topic value directly
      const claimTopics = await ParseClient.getRecords("Claim", ["topic"], [claimTopicValue], ["*"]);

      // Ensure we have valid claim data
      if (!claimTopics || !Array.isArray(claimTopics)) {
        return [];
      }

      // Re-implement identity filtering with proper error handling
      try {
        // Get identities that contain this claim topic
        const identities = await ParseClient.getRecords("Identity", ["claims"], [claimTopicValue]);

        // Only filter if we have valid identities data
        if (identities && Array.isArray(identities) && identities.length > 0) {
          return claimTopics.filter(
            (claim) => claim?.attributes?.identity && identities.some((identity) => identity?.attributes?.identity === claim.attributes.identity)
          );
        }
      } catch (filterError) {
        // If filtering fails, fall back to returning all claims
      }

      // Return all claims if filtering is not possible
      return claimTopics;
    } catch (error) {
      // Log error but don't expose details to client
      console.error("Error in getClaimsForClaimTopics:", error);
      return [];
    }
  }

  async getActiveIdentities() {
    try {
      // Fetch all active identities
      const identities = await ParseClient.getRecords("Identity", ["active"], [true], ["*"]);

      if (identities && identities.length > 0) {
        // Fetch active claims and include the related identityObj and claimTopicObj
        const claims = await ParseClient.getRecords("Claim", ["active"], [true], ["identityObj", "claimTopicObj"]);

        if (claims && claims.length > 0) {
          for (let i = 0; i < identities.length; i++) {
            const identity = identities[i];
            const walletAddress = identity.get("walletAddress"); // Get walletAddress from the Identity

            if (walletAddress) {
              // Query the User class for pepMatched and watchlistMatched columns
              const userRecords = await ParseClient.getRecords("User", ["walletAddress"], [walletAddress], ["pepMatched", "watchlistMatched"]);
              if (userRecords && userRecords.length > 0) {
                const user = userRecords[0]; // Assuming walletAddress is unique and returns one record
                // Add pepMatched and watchlistMatched to the identity response
                if (user.attributes?.pepMatched) {
                  identity.pepMatched = user.attributes.pepMatched;
                }
                if (user.attributes?.watchlistMatched) {
                  identity.watchlistMatched = user.attributes.watchlistMatched;
                }
                if (user.attributes?.username) {
                  identity.email = user.attributes.username;
                }
              }
            }

            // Filter and map claims to the corresponding identity, with a check for valid identityObj
            const activeClaims = claims.filter((claim) => {
              const identityObj = claim.get("identityObj");
              return identityObj && identityObj.id === identity.id; // Check if identityObj exists and matches
            });

            if (activeClaims.length > 0) {
              // Ensure the claims object exists on the identity
              if (!identity.attributes.claims) {
                identity.attributes.claims = { children: [] }; // Initialize claims object if it doesn't exist
              }

              // Assign active claims to children
              identity.attributes.claims.children = activeClaims;

              // Sort the claims by topic
              identity.attributes.claims.children.sort((a, b) => {
                const topicA = a.attributes.claimTopicObj?.attributes?.topic || "";
                const topicB = b.attributes.claimTopicObj?.attributes?.topic || "";
                return topicA > topicB ? 1 : -1;
              });
            }
          }
        }
      }

      return identities;
    } catch (error) {
      console.error("Error fetching active identities or claims:", error);
      throw error;
    }
  }

  async getPendingIdentities() {
    const users = await ParseClient.getRecords("_User", ["pendingApproval", "denied"], [true, false], ["*"]);
    return users;
  }

  async getAddClaimIdentities() {
    const identities = await ParseClient.getRecords("Identity", ["active"], [true], ["*"]);
    if (identities && identities.length > 0) {
      const claims = await ParseClient.getRecords("Claim", ["active"], [true], ["identityObj", "claimTopicObj"]);

      for (let i = 0; i < identities.length; i++) {
        const identity = identities[i];

        // Filter and map claims to the corresponding identity
        const activeClaims = claims.filter((claim) => claim.get("identityObj").id === identity.id);
        identity.attributes.claims = { children: activeClaims };

        // Sort the claims by topic
        identity.attributes.claims.children.sort((a, b) => {
          return a.attributes.claimTopicObj.attributes.topic > b.attributes.claimTopicObj.attributes.topic ? 1 : -1;
        });
      }

      // Filter identities to only include those without claims
      const identitiesWithoutClaims = identities.filter((identity) => !identity.attributes.claims.children.length);

      return identitiesWithoutClaims;
    }

    return [];
  }

  async softRemoveUser(identityAddress) {
    // set pending approval and denied to false
    return await ParseClient.updateExistingRecord("_User", ["walletAddress"], [identityAddress], { denied: true });
  }

  async createIdentity(identity) {
    try {
      console.log("this.signer", this.signer);
      const contract = this.identityFactoryService.connect(this.signer);
      const tx = await contract.createIdentity(identity);
      await tx.wait();
      return tx;
    } catch (error) {
      console.log("createIdentity error", error);
    }
  }

  async getIdentity(address, retries = 5, delay = 1000) {
    const contract = this.identityFactoryService.connect(this.signer);

    for (let attempt = 0; attempt < retries; attempt++) {
      const tx = await contract.getIdentity(address);

      if (tx && tx !== "0x0000000000000000000000000000000000000000") {
        // Ensure valid identity
        console.log(`✅ Identity found: ${tx}`);
        return tx;
      }

      console.warn(`🔄 Identity not found yet, retrying... Attempt ${attempt + 1}/${retries}`);
      await new Promise((resolve) => setTimeout(resolve, delay)); // Wait before retrying
    }

    console.error("❌ Identity retrieval failed after multiple attempts.");
    throw new Error("Identity not found");
  }

  async updateIdentity(identity, identityData) {
    const maxRetries = 3,
      baseDelay = 4000;

    if (!identityData) {
      console.warn("No identity data provided, skipping update.");
      return null;
    }

    // Retry helper function
    async function retryWithBackoff(fn, retries, delay) {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const result = await fn();
          if (result) return result; // Return if successful
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error);
        }

        if (attempt < retries) {
          const waitTime = delay * attempt; // Exponential backoff
          console.log(`Retrying in ${waitTime / 1000} seconds...`);
          await new Promise((resolve) => setTimeout(resolve, waitTime));
        }
      }
      console.error("Update failed after max retry attempts.");
      return null;
    }

    // Perform the update with retry
    return retryWithBackoff(() => ParseClient.updateExistingRecord("Identity", ["walletAddress"], [identity], identityData), maxRetries, baseDelay);
  }

  async createParseIdentity(identity, identityData) {
    return await ParseClient.createRecord("_User", ["address"], [identity], identityData);
  }

  async isUser(identityAddress) {
    const user = await ParseClient.getRecord("_User", ["walletAddress"], [identityAddress]);
    return user ? true : false;
  }

  async approveUser(identityAddress) {
    return await ParseClient.updateExistingRecord("_User", ["walletAddress"], [identityAddress], { pendingApproval: false });
  }

  async addIdentity(identity, identityData) {
    const contract = this.identityRegistryService.connect(this.signer);
    const tx = await contract.addIdentity(identity, identityData);
    await tx.wait();
    return tx;
  }

  async getDigitalIdentity(id) {
    // Assembled server-side by the "getIdentityWithClaimTopics" cloud function (master key):
    // joins ClaimAdded__e -> Claim -> ClaimTopic -> _User and returns the identity with its
    // active claims (claim name, issue date, block hash, transaction hash, trusted issuer
    // address + email) in a single round-trip.
    const result = await Parse.Cloud.run("getIdentityWithClaimTopics", { id });

    // Removed-claim history (added/removed dates) comes from the separate
    // "getIdentityClaimHistory" cloud function, which replays the raw
    // ClaimAdded__e/ClaimRemoved__e event tables — getIdentityWithClaimTopics above
    // only ever tracks the currently-active claim set.
    if (result?.identity) {
      try {
        const { claimHistory } = await Parse.Cloud.run("getIdentityClaimHistory", { identity: result.identity });
        result.removedClaims = (claimHistory || []).filter((claim) => !claim.active);
      } catch (err) {
        console.error("Failed to fetch claim history:", err);
        result.removedClaims = [];
      }
    }

    return result;
  }

  async getRemovedIdentityDetails(id) {
    // Backs the Removed Identity card. The Removed tab's "View" passes the _User
    // objectId, so this resolves _User -> wallet -> (inactive) Identity -> ONCHAINID
    // -> IdentityRemoved__e and returns the trusted issuer who removed the identity
    // (wallet + email), the transaction hash and the removal date. Returns null when
    // there is no removal event for this user.
    return await Parse.Cloud.run("getRemovedIdentityDetails", { id });
  }

  async batchAddIdentity(identities, identityDatas) {
    const contract = this.identityRegistryService.connect(this.signer);
    const tx = await contract.batchAddIdentity(identities, identityDatas);
    await tx.wait();
    return tx;
  }

  async removeIdentity(identity) {
    const contract = this.identityFactoryService.connect(this.signer);
    const tx = await contract.removeIdentity(identity);
    await tx.wait();
    return tx;
  }

  async unregisterIdentity(identity) {
    const contract = this.identityRegistryService.connect(this.signer);
    const tx = await contract.removeIdentity(identity);
    await tx.wait();
    return tx;
  }

  async setClaims(identity, claims) {
    const contract = this.identityRegistryService.connect(this.signer);
    const tx = await contract.setClaims(identity, claims);
    const txs = await tx.wait();

    return txs;
  }

  async addClaim(identity, claimTopic, claim) {
    const contract = this.identityRegistryService.connect(this.signer);
    const tx = await contract.addClaim(identity, claimTopic, claim);
    await tx.wait();
    return tx;
  }

  async removeClaim(identity, claimTopicObject) {
    try {
      console.log("claimTopicObject", claimTopicObject);
      // Extract the 'topic' field if claimTopicObject is an object
      const claimTopic = claimTopicObject.topic;

      // Ensure claimTopic is a valid BigNumber-compatible value
      if (!claimTopic || isNaN(Number(claimTopic))) {
        throw new Error(`Invalid claimTopic value: ${claimTopic}`);
      }

      const contract = this.identityRegistryService.connect(this.signer);
      const tx = await contract.removeClaim(identity, claimTopic); // Pass valid claimTopic
      await tx.wait();
      return tx;
    } catch (error) {
      console.error("Error in removeClaim:", error);
    }
  }

  async contains(userAddress) {
    return await this.identityRegistryService.contains(userAddress);
  }

  async isVerified(userAddress) {
    try {
      if (!this.dedicatedProvider) {
        throw new Error("No dedicated provider available for isVerified()");
      }

      const contract = new ethers.Contract(this.identityRegistryService.address, this.identityRegistryAbi, this.dedicatedProvider);

      console.log("Using dedicated provider for isVerified:", contract.provider?.connection?.url || "unknown");

      return await contract.isVerified(userAddress);
    } catch (err) {
      console.error("❌ Error in isVerified():", err);
      throw err;
    }
  }

  async identity(userAddress) {
    return await this.identityRegistryService.identity(userAddress);
  }

  async getRegistryUsers() {
    return await this.identityRegistryService.getRegistryUsers();
  }

  async isRegistryUser(registryUser) {
    return await this.identityRegistryService.isRegistryUser(registryUser);
  }

  async getClaims(registryUser) {
    return await this.identityRegistryService.getClaims(registryUser);
  }

  async getClaim(registryUser, claimTopic) {
    return this.identityRegistryService && (await this.identityRegistryService.getClaim(registryUser, claimTopic));
  }

  async hasClaim(registryUser, claimTopic) {
    return await this.identityRegistryService.hasClaim(registryUser, claimTopic);
  }

  async addTrustedIssuer(trustedIssuer, claimTopics) {
    const contract = this.trustedIssuersRegistryService.connect(this.signer);
    const tx = await contract.addTrustedIssuer(trustedIssuer, claimTopics);
    await tx.wait();
    return tx;
  }

  // TrustedIssuer is CLP-locked to master (src/auth/policy.ts), so this can no
  // longer poll/write the class directly from the browser — same poll for the
  // indexer-created row, now server-side with the master key.
  async updateTrustedIssuer(data) {
    if (!data || !data.issuer) {
      console.warn("🚨 Invalid data provided, skipping update.");
      return null;
    }

    try {
      const result = await Parse.Cloud.run("updateTrustedIssuer", data);
      return result;
    } catch (error) {
      console.error(`❌ updateTrustedIssuer failed for issuer '${data.issuer}':`, error);
      return null;
    }
  }

  async removeTrustedIssuer(trustedIssuer) {
    const contract = this.trustedIssuersRegistryService.connect(this.signer);
    const tx = await contract.removeTrustedIssuer(trustedIssuer);
    await tx.wait();
    return tx;
  }

  async updateIssuerClaimTopics(trustedIssuer, claimTopics) {
    const contract = this.trustedIssuersRegistryService.connect(this.signer);
    const tx = await contract.updateIssuerClaimTopics(trustedIssuer, claimTopics);
    await tx.wait();
    return tx;
  }

  // Reshape a plain `getTrustedIssuers` cloud-function row into the Parse-object-like
  // shape the issuer pages consume (`item.id` + `item.attributes.*`). TrustedIssuer is
  // CLP-locked to master, so these rows now come from the cloud function rather than a
  // direct `TrustedIssuer` class query, which the CLP rejects.
  _toIssuerRecord(row) {
    return {
      id: row.id,
      attributes: {
        issuer: row.issuer,
        verifierName: row.verifierName,
        claimTopics: row.claimTopics ?? [],
        active: row.active,
        transactionHash: row.transactionHash,
        eventId: row.eventId,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
      },
    };
  }

  async getTrustedIssuers() {
    const { issuers = [] } = await Parse.Cloud.run("getTrustedIssuers", { active: true });
    return issuers.map((issuer) => this._toIssuerRecord(issuer));
  }

  async isTrustedIssuer(issuer) {
    return await this.trustedIssuersRegistryService.isTrustedIssuer(issuer);
  }

  async getTrustedIssuerClaimTopics(trustedIssuer) {
    return await this.trustedIssuersRegistryService.getTrustedIssuerClaimTopics(trustedIssuer);
  }

  async hasClaimTopic(issuer, claimTopic) {
    return await this.trustedIssuersRegistryService.hasClaimTopic(issuer, claimTopic);
  }

  async getTokenProjects() {
    try {
      const TokenProject = Parse.Object.extend("TokenProject");
      const query = new Parse.Query(TokenProject);

      // query.equalTo("status", "active");

      // Sort by creation date (newest first)
      query.descending("createdAt");

      // Limit results if needed
      query.limit(100);

      const results = await query.find();

      return results.map((project) => ({
        id: project.id,
        title: project.get("title"),
        description: project.get("description"),
        // Add other fields
        tradeDeadId: project.get("tradeDeadId"),
        interestRate: project.get("interestRate"),
        industryTemplate: project.get("industryTemplate"),
        logo: project.get("logo"),
        updatedAt: project.get("updatedAt"),
        createdAt: project.get("createdAt"),
        fields: project.get("fields"),
        projectInfo: project.get("projectInfo"),
      }));
    } catch (error) {
      console.error("Error fetching token projects:", error);
      throw error;
    }
  }
}

export default BlockchainService;
